import {toDay} from "@/libs/helper";
import {ApexOptions} from "apexcharts";
import dynamic from "next/dynamic";
import {useEffect, useState} from "react";
const Chart = dynamic(() => import("react-apexcharts"), {ssr: false});
const itemsPerPage = 7; // Number of items to show per page
interface ChartSeries {
  name: string;
  data: number[];
}
export const TrendsChart: React.FC<any> = ({statistics}) => {
  const [chartPage, setChartPage] = useState(0);
  const [chartTotalPages, setChartTotalPages] = useState(0);
  const [trendsChartOptions, setTrendsChartOptions] = useState<{
    options: ApexOptions;
    series: ChartSeries[];
  }>({
    options: {},
    series: []
  });
  useEffect(() => {
    const trends = statistics?.trends || [];

    const categories = trends.map((item: any, k: number) => toDay(item.time)); // Ensure 'day' is a key like "01", "02"
    const totalPages = Math.ceil(categories.length / itemsPerPage);
    setChartTotalPages(totalPages);

    const start = chartPage * itemsPerPage;
    const end = start + itemsPerPage;
    const paginatedCategories = categories.slice(start, end);
    const paginatedTrends = trends.slice(start, end);
    // const safeGet = (arr: any[], key: string) =>
    //   arr.map(item => toKilometers(item[key]) || 0); // Convert meters to KM
    const safeGet = (arr: any[] = [], key: string): number[] => {
      if (!Array.isArray(arr) || arr.length === 0) return [];
      //toKilometers(item?.[key] ?? 0)
      return arr.map(item => item?.[key] ?? 0);
    };

    const below40Kmph = safeGet(paginatedTrends, "below40Kmph");
    const between40To50Kmph = safeGet(paginatedTrends, "between40To50Kmph");
    const between50To60Kmph = safeGet(paginatedTrends, "between50To60Kmph");
    const between60To70Kmph = safeGet(paginatedTrends, "between60To70Kmph");
    const between70To80Kmph = safeGet(paginatedTrends, "between70To80Kmph");
    const between80To90Kmph = safeGet(paginatedTrends, "between80To90Kmph");
    const above90Kmph = safeGet(paginatedTrends, "above90Kmph");

    setTrendsChartOptions({
      options: {
        chart: {
          type: "bar",
          height: 120,
          stacked: true,
          toolbar: {show: false},
          zoom: {
            enabled: true,
            type: "xy",
            autoScaleYaxis: true
          }
        },
        plotOptions: {
          bar: {
            horizontal: false,
            borderRadius: 3,
            barHeight: "65%"
          }
        },
        xaxis: {
          categories: paginatedCategories,
          labels: {
            show: true,
            style: {
              fontSize: "10px"
            }
          }
        },
        yaxis: {
          labels: {
            formatter: (val: number) => `${val} km`
          }
        },
        dataLabels: {enabled: false},
        legend: {
          position: "top",
          horizontalAlign: "right",
          show: false
        },
        grid: {
          padding: {left: 0, right: 0, top: 10, bottom: 0}
        },
        colors: [
          "#22c55e", // > 40 KM
          "#16a34a", // 40–50
          "#15803d", // 50–60
          "#eab308", // 60–70
          "#ca8a04", // 70–80
          "#f87171", // 80–90
          "#b91c1c" // > 90
        ]
      },
      series: [
        {name: "> 40 KM", data: below40Kmph},
        {name: "40 50 KM", data: between40To50Kmph},
        {name: "50 60 KM", data: between50To60Kmph},
        {name: "60 70 KM", data: between60To70Kmph},
        {name: "70 80 KM", data: between70To80Kmph},
        {name: "80 90 KM", data: between80To90Kmph},
        {name: "> 90 KM", data: above90Kmph}
      ]
    });
  }, [statistics?.trends, chartPage]);

  return (
    <div className="flex w-full flex-col">
      {trendsChartOptions.series.length > 0 && (
        <Chart
          options={trendsChartOptions.options}
          series={trendsChartOptions.series}
          type="bar"
          height={120}
        />
      )}

      <div className="flex items-center justify-between">
        <button
          onClick={() => setChartPage(p => Math.max(p - 1, 0))}
          disabled={chartPage === 0}
          className="flex items-center gap-1 rounded bg-gray-200 px-2 py-1 text-sm disabled:opacity-50"
        >
          <svg
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <line
              x1="20"
              y1="12"
              x2="4"
              y2="12"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <line
              x1="10"
              y1="6"
              x2="4"
              y2="12"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <line
              x1="10"
              y1="18"
              x2="4"
              y2="12"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          Prev
        </button>

        <span className="text-xs text-gray-600">
          Page {chartPage + 1} of {chartTotalPages}
        </span>

        <button
          onClick={() =>
            setChartPage(p => Math.min(p + 1, chartTotalPages - 1))
          }
          disabled={chartPage >= chartTotalPages - 1}
          className="flex items-center gap-1 rounded bg-gray-200 px-2 py-1 text-sm disabled:opacity-50"
        >
          Next
          <svg
            className="h-4 w-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <line
              x1="4"
              y1="12"
              x2="20"
              y2="12"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <line
              x1="14"
              y1="6"
              x2="20"
              y2="12"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <line
              x1="14"
              y1="18"
              x2="20"
              y2="12"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};
